import React, { useState } from 'react';
import moment from 'moment';
import 'moment/locale/es';
import { Grid } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PercentIcon from '@mui/icons-material/Percent';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PaymentsIcon from '@mui/icons-material/Payments';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DoneAllIcon from '@mui/icons-material/DoneAll';

import ClientCount from './homeInfoComponents/ClientCount';
import ActiveCredits from './homeInfoComponents/ActiveCredits';
import VecidoCredits from './homeInfoComponents/VecidoCredits';
import RecuperacionDiaria from './homeInfoComponents/RecuperacionDiaria';
import PendieteCredits from './homeInfoComponents/PendieteCredits';
import MoraTotal from './homeInfoComponents/MoraTotal';
import BlobSize from './homeInfoComponents/BlobSize';
import KpiPanel from './homeInfoComponents/KpiPanel';
import { getResumenPanelAsync } from '../../api/Dashboard';
import { formatearMonto } from '../../helpers/formato';
import { useDatoPanel } from './useDatoPanel';
import { porcentajeConDecimal } from './panel';

// Filas de cuatro y de tres, como antes: cartera (cuántos), saldos, hoy y el mes, y lo demás.
const CUARTO = { xs: 12, sm: 6, md: 3 };
const TERCIO = { xs: 12, sm: 6, md: 4 };

/**
 * Indicadores del inicio. Los siete de antes piden su dato cada uno; los siete nuevos salen juntos
 * de GetResumenPanel y comparten la carga y el error (un "Reintentar" en cualquiera los recarga).
 * Cada uno es un KpiCard con su propio Paper: no se envuelven en otro.
 */
const HomeInfo = () => {
	const [activos, setActivos] = useState(null);
	const [resumen, setResumen] = useState({});
	const estado = useDatoPanel(getResumenPanelAsync, (data) => setResumen(data.result ?? {}));
	const comun = { cargando: estado.cargando, error: estado.error, onReintentar: estado.recargar };
	// Si falla GetResumenPanel fallan las siete: solo la primera (Cartera Activa) lo anuncia.
	const callado = { ...comun, anunciarError: false };

	const hoy = moment().format('YYYY-MM-DD');
	const inicioMes = moment().startOf('month').format('YYYY-MM-DD');
	const mes = moment().locale('es').format('MMMM');
	const enElMes = `En ${mes}`;

	const indiceMora = porcentajeConDecimal(resumen.moraTotal, resumen.carteraActiva);
	const cumplimiento = porcentajeConDecimal(resumen.cobradoHoy, resumen.esperadoHoy);
	const colocados = resumen.creditosColocadosMes ?? 0;

	return (
		<Grid container spacing={2} component="section" aria-label="Indicadores">
			<Grid item {...CUARTO}>
				<ClientCount />
			</Grid>
			<Grid item {...CUARTO}>
				<ActiveCredits onValor={setActivos} />
			</Grid>
			<Grid item {...CUARTO}>
				<VecidoCredits activos={activos} />
			</Grid>
			<Grid item {...CUARTO}>
				<PendieteCredits />
			</Grid>

			<Grid item {...TERCIO}>
				<KpiPanel
					{...comun}
					titulo="Cartera Activa"
					valor={resumen.carteraActiva}
					formato="monto"
					detalle="Saldo por cobrar de los créditos activos"
					icono={<AccountBalanceWalletIcon />}
					tono="primary"
					destino="/credits"
					permiso="CREDITS VER"
					ayuda="Ver Créditos"
				/>
			</Grid>
			<Grid item {...TERCIO}>
				<MoraTotal />
			</Grid>
			<Grid item {...TERCIO}>
				<KpiPanel
					{...callado}
					titulo="Índice de Mora"
					valor={indiceMora ?? '—'}
					detalle="Parte de la cartera activa que está atrasada"
					icono={<PercentIcon />}
					tono="error"
				/>
			</Grid>

			<Grid item {...CUARTO}>
				<RecuperacionDiaria />
			</Grid>
			<Grid item {...CUARTO}>
				<KpiPanel
					{...callado}
					titulo="Cumplimiento de Hoy"
					valor={cumplimiento ?? '—'}
					detalle={
						cumplimiento
							? `Cobrado ${formatearMonto(resumen.cobradoHoy)} · Vence hoy ${formatearMonto(resumen.esperadoHoy)}`
							: 'Hoy no vence ninguna cuota'
					}
					icono={<TaskAltIcon />}
					tono="success"
					destino={`/r-recuperacion/t/${hoy}/${hoy}`}
					permiso="RECUPERAC"
					ayuda="Ver el reporte de Recuperación de hoy"
				/>
			</Grid>
			<Grid item {...CUARTO}>
				<KpiPanel
					{...callado}
					titulo="Recuperación del Mes"
					valor={resumen.recuperacionMes}
					formato="monto"
					detalle={enElMes}
					icono={<CalendarMonthIcon />}
					tono="success"
					destino={`/r-recuperacion/t/${inicioMes}/${hoy}`}
					permiso="RECUPERAC"
					ayuda="Ver el reporte de Recuperación del mes"
				/>
			</Grid>
			<Grid item {...CUARTO}>
				<KpiPanel
					{...callado}
					titulo="Colocación del Mes"
					valor={resumen.colocacionMes}
					formato="monto"
					detalle={`${colocados} ${colocados === 1 ? 'crédito' : 'créditos'} en ${mes}`}
					icono={<PaymentsIcon />}
					tono="primary"
					destino={`/r-desembolsos/t/${inicioMes}/${hoy}`}
					permiso="RDESEMBOLSO"
					ayuda="Ver el reporte de Desembolsos del mes"
				/>
			</Grid>

			<Grid item {...TERCIO}>
				<KpiPanel
					{...callado}
					titulo="Clientes Nuevos del Mes"
					valor={resumen.clientesNuevosMes}
					formato="entero"
					detalle={enElMes}
					icono={<PersonAddIcon />}
					tono="primary"
					destino="/clients"
					permiso="CLIENTS VER"
					ayuda="Ver Clientes"
				/>
			</Grid>
			<Grid item {...TERCIO}>
				<KpiPanel
					{...callado}
					titulo="Créditos Cancelados del Mes"
					valor={resumen.creditosCanceladosMes}
					formato="entero"
					detalle={enElMes}
					icono={<DoneAllIcon />}
					tono="success"
				/>
			</Grid>
			<Grid item {...TERCIO}>
				<BlobSize />
			</Grid>
		</Grid>
	);
};

export default HomeInfo;
